import type { RunUpdate } from "../shared/hub-capture.ts";
import type { HubContext } from "../shared/hub-catalog.ts";
import type {
  CatalogEntry,
  Command,
  Identity,
  Invitation,
  LabAction,
  ParticipantView,
  PresenterView,
  RunSummary,
  RunPurpose,
  RunView,
  StoryDraft,
} from "../shared/types.ts";

const deviceKey = "sensavera-lab-device";
// LAN previews may use HTTP; getRandomValues is available there even when
// randomUUID's secure-context API is not.
export function randomId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
    "",
  );
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
}
function clientId(): string {
  let id = sessionStorage.getItem(deviceKey);
  if (!id) {
    id = randomId();
    sessionStorage.setItem(deviceKey, id);
  }
  return id;
}
/** Per document: duplicated tabs may copy sessionStorage, but never this token. */
export const narrationId = randomId();
/** `status` 0 means no response arrived, so the server may or may not have acted. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}
async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      credentials: "same-origin",
      signal: AbortSignal.timeout(10000),
      ...options,
      headers: { "Content-Type": "application/json", ...options.headers },
    });
  } catch {
    throw new ApiError(
      "The lab could not be reached. Check the connection and try again.",
      0,
    );
  }
  const data = await response.json().catch(() => null);
  if (!response.ok || data === null)
    throw new ApiError(
      data?.error?.message ??
        data?.message ??
        (response.ok
          ? "The lab sent a response that could not be read."
          : "The lab is temporarily unavailable (" + response.status + ")."),
      response.status,
    );
  return data as T;
}
const post = <T>(path: string, body: unknown = {}) =>
  request<T>(path, { method: "POST", body: JSON.stringify(body) });
const runPath = (id: string, participant = false) =>
  "/api/" +
  (participant ? "participant/" : "") +
  "runs/" +
  encodeURIComponent(id);
export const runEventsPath = (id: string, participant: boolean) =>
  runPath(id, participant) +
  "/events" +
  (participant ? "" : "?hubCapture=attached");
export interface InvitationPreview {
  label: string;
  runTitle: string;
  runId: string;
}
export const api = {
  identity: () =>
    request<{ identity: Identity | null; localAccess: boolean }>("/api/auth"),
  login: (password: string) => post<Identity>("/api/auth/login", { password }),
  localLogin: () => post<Identity>("/api/auth/local"),
  logout: () => post<void>("/api/auth/logout"),
  catalog: () => request<CatalogEntry[]>("/api/catalog"),
  hubCatalog: () =>
    request<
      Pick<
        HubContext,
        "id" | "client" | "organization" | "campaign" | "variant" | "storyIds"
      >[]
    >("/api/hub-catalog"),
  runs: (purpose: RunPurpose = "demo") =>
    request<RunSummary[]>("/api/runs?purpose=" + purpose),
  createRun: (input: {
    title: string;
    storyIds: string[];
    purpose?: RunPurpose;
  }) => post<PresenterView>("/api/runs", input),
  view: (id: string, eventIndex?: number) =>
    request<PresenterView>(
      runPath(id) + (eventIndex !== undefined ? "?at=" + eventIndex : ""),
    ),
  participantView: (id: string) => request<ParticipantView>(runPath(id, true)),
  preview: (id: string, sessionId: string) =>
    request<ParticipantView>(
      runPath(id) + "/preview/" + encodeURIComponent(sessionId),
    ),
  command: (id: string, command: Command, participant = false) =>
    request<RunUpdate>(runPath(id, participant) + "/commands", {
      method: "POST",
      body: JSON.stringify(command),
      headers: participant ? {} : { "X-Lab-Hub-Capture": "attached" },
    }),
  invitations: (id: string) =>
    request<Invitation[]>(runPath(id) + "/invitations"),
  invite: (id: string, sessionId: string) =>
    post<Invitation>(runPath(id) + "/invitations", { sessionId }),
  revoke: (id: string, invitationId: string) =>
    post<void>(
      runPath(id) +
        "/invitations/" +
        encodeURIComponent(invitationId) +
        "/revoke",
    ),
  fork: (id: string, eventIndex: number, title?: string) =>
    post<PresenterView>(runPath(id) + "/fork", { eventIndex, title }),
  exportDraft: (id: string, storyId: string) =>
    request<{
      draft: StoryDraft;
      canonicalPath: string;
      canonicalDiff: StoryDraft["diff"];
      canonicalRebaseRequired: boolean;
    }>(runPath(id) + "/drafts/" + encodeURIComponent(storyId) + "/export"),
  invitation: (token: string) =>
    request<InvitationPreview>("/api/invitations/" + encodeURIComponent(token)),
  join: (token: string) =>
    post<unknown>("/api/join/" + encodeURIComponent(token), {
      clientId: clientId(),
    }),
};
export function makeCommand(
  action: LabAction,
  controlRevision?: number,
): Command {
  return {
    id: randomId(),
    clientId: clientId(),
    source: "manual",
    action,
    ...(controlRevision !== undefined ? { controlRevision } : {}),
  };
}
